import { Button } from "@totalenergiescode/mobility-business-rev-design-system";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useResynchroniseContracts } from "@/api/graphql/migration/resynchronize/hooks";
import { MiddleMsResynchronisationRequestType } from "@/api/graphql/types/graphql";
import { InputEvent } from "@/types/inputElements";

import { StyledTextArea } from "../styles";
import {
  DRAWER_WIDTH,
  MAX_ACCOUNTS_INPUT_LENGTH,
  MAX_ROWS,
  RESYNCHRONISATION_TYPES,
} from "./constants";
import {
  StyledContentContainer,
  StyledDrawer,
  StyledDrawerInnerContainer,
  StyledSelect,
  StyledText,
} from "./styles";
import { MigrationDrawerProps } from "./types";
import { splitOnLinesOf } from "./utils";

export const MigrationDrawer: React.FC<MigrationDrawerProps> = ({
  setIsMigrationModalOpen,
  isMigrationModalOpen,
}) => {
  const { t: tMigrationReport } = useTranslation("migrationReport");
  const [accountsInput, setAccountsInput] = useState<string>("");
  const [isError, setIsError] = useState<boolean>(false);
  const [resynchronisationType, setResynchronisationType] =
    useState<string>("");

  const close = () => {
    setIsMigrationModalOpen(false);
    setAccountsInput("");
  };

  const { mutateAsync: synchroniseContracts, isPending } =
    useResynchroniseContracts(close);

  const handleSyncData = () => {
    const accounts = splitOnLinesOf(accountsInput);
    if (accounts.length > MAX_ACCOUNTS_INPUT_LENGTH) {
      setIsError(true);
    } else {
      synchroniseContracts({
        customerAccountNumbers: accounts,
        type: resynchronisationType as MiddleMsResynchronisationRequestType,
      });
    }
  };

  return (
    <StyledDrawer
      isOpen={isMigrationModalOpen}
      width={DRAWER_WIDTH}
      dataTestId='migration-report__sync-data-drawer'
      onClose={() => setIsMigrationModalOpen(false)}
    >
      <StyledDrawerInnerContainer>
        <StyledContentContainer>
          <StyledText variant='title5'>
            {tMigrationReport("migrationReport.drawer.title")}
          </StyledText>

          <StyledTextArea
            multiline
            maxRows={MAX_ROWS}
            placeholder={tMigrationReport(
              "migrationReport.drawer.inputPlaceholder",
            )}
            dataTestId='migration-report__sync-data-input'
            value={accountsInput}
            onChange={(e: InputEvent) => {
              setAccountsInput(e.target.value);
              setIsError(false);
            }}
            isError={isError}
            errorText={tMigrationReport(
              "migrationReport.errorMessage.maxAccountsSyncError",
            )}
          />

          <StyledSelect
            label={tMigrationReport("migrationReport.drawer.sync.typeLabel")}
            dataTestId='migration-report__sync-data-input'
            onChange={(e) => {
              setResynchronisationType(e.target.value?.toString());
            }}
            options={RESYNCHRONISATION_TYPES(tMigrationReport)}
            type='options'
          />
        </StyledContentContainer>

        <Button
          variant='primary'
          isLowercase
          dataTestId='migration-report__sync-data-button'
          disabled={isPending || !accountsInput || !resynchronisationType}
          buttonText={tMigrationReport("migrationReport.drawer.syncButton")}
          onClick={handleSyncData}
        />
      </StyledDrawerInnerContainer>
    </StyledDrawer>
  );
};
